import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { arrayOf, shape, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { IconClose } from '../../components';

import css from './FormHelpDrawer.module.css';

const FormHelpDrawer = props => {
  const { sections, className } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [activeId, setActiveId] = useState(sections[0]?.id || null);
  const suppressObserverUntil = useRef(0);
  const suppressTimeoutRef = useRef(null);

  const sectionById = useMemo(
    () => sections.reduce((acc, section) => ({ ...acc, [section.id]: section }), {}),
    [sections]
  );

  useEffect(() => {
    if (!isOpen) return undefined;

    const targets = sections
      .map(section => document.getElementById(section.anchorId))
      .filter(Boolean);

    if (!targets.length) return undefined;

    const observer = new IntersectionObserver(
      entries => {
        const visible = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visible[0]) {
          if (Date.now() < suppressObserverUntil.current) {
            return;
          }
          const id = visible[0].target.getAttribute('data-help-anchor');
          if (id) setActiveId(id);
        }
      },
      {
        root: null,
        rootMargin: '-35% 0px -55% 0px',
        threshold: [0.1, 0.25, 0.5, 0.75],
      }
    );

    targets.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, [isOpen, sections]);

  const activeSection = sectionById[activeId] || sections[0];


  const handleSelect = section => {
    setActiveId(section.id);
    const element = document.getElementById(section.anchorId);
    if (element) {
      suppressObserverUntil.current = Date.now() + 700;
      if (suppressTimeoutRef.current) {
        clearTimeout(suppressTimeoutRef.current);
      }
      suppressTimeoutRef.current = setTimeout(() => {
        suppressObserverUntil.current = 0;
        suppressTimeoutRef.current = null;
      }, 750);
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(
    () => () => {
      if (suppressTimeoutRef.current) {
        clearTimeout(suppressTimeoutRef.current);
      }
    },
    []
  );

  const content = (
    <>
      <button
        type="button"
        className={classNames(css.toggleButton, { [css.toggleButtonHidden]: isOpen })}
        onClick={() => setIsOpen(true)}
      >
        <FormattedMessage id="HelpDrawer.needHelp" />
      </button>

      {isOpen && <div className={css.overlay} onClick={() => setIsOpen(false)} />}

      <aside className={classNames(css.drawer, className, { [css.open]: isOpen })}>
        <header className={css.header}>
          <h3 className={css.title}>
            <FormattedMessage id="HelpDrawer.title" />
          </h3>
          <button
            type="button"
            className={css.closeButton}
            onClick={() => setIsOpen(false)}
            aria-label="Close"
          >
            <IconClose />
          </button>
        </header>

        <div className={css.body}>
          <nav className={css.nav}>
            {sections.map(section => (
              <button
                key={section.id}
                type="button"
                className={classNames(css.navItem, {
                  [css.navItemActive]: section.id === activeId,
                })}
                onClick={() => handleSelect(section)}
              >
                {section.title}
              </button>
            ))}
          </nav>

          {activeSection && (
            <div className={css.content}>
              <h4 className={css.contentTitle}>{activeSection.title}</h4>
              <p className={css.contentBody}>{activeSection.body}</p>
            </div>
          )}
        </div>

        <div className={css.footer}>
          <button
            type="button"
            className={css.backButton}
            onClick={() => setIsOpen(false)}
          >
            <FormattedMessage id="HelpDrawer.backToForm" />
          </button>
        </div>
      </aside>
    </>
  );

  if (typeof document !== 'undefined') {
    return createPortal(content, document.body);
  }

  return content;
};

FormHelpDrawer.propTypes = {
  sections: arrayOf(
    shape({
      id: string.isRequired,
      title: string.isRequired,
      body: string.isRequired,
      anchorId: string.isRequired,
    })
  ).isRequired,
  className: string,
};

FormHelpDrawer.defaultProps = {
  className: null,
};

export default FormHelpDrawer;
